import { environment } from '../../../../environments/environment';
import { Component, Input, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../../../core/services/auth.service';

interface NavItem { label: string; icon: string; route: string; }
interface NavSection { title?: string; items: NavItem[]; }

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  styleUrls: ['./sidebar.component.scss'],
  template: `
    <aside class="sidebar" [class.is-open]="open" [attr.aria-hidden]="null">

      <!-- Logo -->
      <div class="sidebar-logo">
        <div class="logo-wrap">
          <img src="${environment.logoUrl}"
               alt="Happy Meal Prep" class="logo-img" onerror="this.style.display='none'">
          <div class="logo-text">
            <span class="logo-title">Happy Ordering</span>
            <span class="logo-sub">Super Admin</span>
          </div>
        </div>
      </div>

      <!-- Nav -->
      <nav class="sidebar-nav">
        <ng-container *ngFor="let section of sections">
          <div class="nav-section-label" *ngIf="section.title">{{ section.title }}</div>
          <a *ngFor="let item of section.items"
             [routerLink]="item.route"
             class="nav-item"
             [class.active]="isActive(item.route)">
            <i class="nav-icon" [ngClass]="item.icon"></i>
            <span class="nav-label">{{ item.label }}</span>
          </a>
        </ng-container>
      </nav>

      <!-- User footer -->
      <div class="sidebar-footer">
        <div class="user-row">
          <div class="user-avatar">{{ initials() }}</div>
          <div class="user-info">
            <div class="user-name">{{ userName() }}</div>
            <div class="user-role">Super Admin</div>
          </div>
          <button class="logout-btn" (click)="logout()" title="Sign out">
            <i class="fa-solid fa-arrow-right-from-bracket"></i>
          </button>
        </div>
      </div>
    </aside>
  `
})
export class SidebarComponent {
  /**
   * Only consulted below the drawer breakpoint. Above it the sidebar is a fixed
   * column and this class does nothing — the CSS never reads it, so there is no
   * state to keep in sync on desktop.
   */
  @Input() open = false;

  readonly logoUrl = environment.logoUrl;
  currentRoute = signal('');

  /**
   * Grouping only — every label, icon and route below is carried over unchanged
   * from the previous arrangement. Sections were renamed, reordered and their
   * membership changed; nothing about the items themselves did.
   *
   * Salespeople was held back while the feature was designed but unbuilt —
   * there was no /salespeople route for it to point at. The list page exists
   * now, so the entry is here. See sidebar.component.spec.ts, which pins every
   * route to one the router actually registers.
   */
  readonly sections: NavSection[] = [
    {
      title: 'Platform',
      items: [
        { label: 'Dashboard',             icon: 'fa-solid fa-gauge-high',     route: '/dashboard' },
        { label: 'Clients',               icon: 'fa-solid fa-building',       route: '/clients' },
        { label: 'Salespeople',           icon: 'fa-solid fa-user-tie',       route: '/salespeople' },
        { label: 'Checklist templates',   icon: 'fa-solid fa-list-check',     route: '/checklist-templates' },
        { label: 'Administrators',        icon: 'fa-solid fa-user-shield',    route: '/admins' },
        { label: 'Roles',                 icon: 'fa-solid fa-key',            route: '/roles' },
        { label: 'Plugins',               icon: 'fa-solid fa-plug',           route: '/plugins' },
      ]
    },
    {
      title: 'Billing & Delivery',
      items: [
        { label: 'Chargebee Billing', icon: 'fa-solid fa-file-invoice-dollar', route: '/chargebee-billing' },
        { label: 'Wallet',           icon: 'fa-solid fa-wallet',     route: '/wallet' },
        { label: 'Billing Throttle', icon: 'fa-solid fa-gauge',      route: '/billing-throttle' },
        { label: 'Uber Direct',      icon: 'fa-solid fa-truck-fast', route: '/uber-direct' },
      ]
    },
    {
      title: 'Security',
      items: [
        { label: 'Checkout Guard', icon: 'fa-solid fa-shield-halved', route: '/checkout-guard' },
      ]
    },
    {
      title: 'Content',
      items: [
        { label: 'Academy',        icon: 'fa-solid fa-graduation-cap', route: '/academy' },
        { label: 'Guides',         icon: 'fa-solid fa-book',           route: '/guides' },
        { label: 'WordPress Sync', icon: 'fa-brands fa-wordpress',     route: '/wordpress-sync' },
      ]
    },
    {
      title: 'Communications',
      items: [
        { label: 'Announcements',   icon: 'fa-solid fa-bullhorn',            route: '/announcements' },
        { label: 'Email Templates', icon: 'fa-solid fa-envelope-open-text',  route: '/email-templates' },
        { label: 'Email Logs',      icon: 'fa-solid fa-clipboard-list',      route: '/email-logs' },
        { label: 'Sender Domains',  icon: 'fa-solid fa-at',                  route: '/sender-domains' },
        { label: 'Mail Providers',  icon: 'fa-solid fa-paper-plane',         route: '/mail-providers' },
      ]
    },
    {
      title: 'Feedback',
      items: [
        { label: 'Feature Suggestions', icon: 'fa-solid fa-lightbulb', route: '/feature-suggestions' },
      ]
    },
    {
      title: 'System & Operations',
      items: [
        { label: 'Backups',         icon: 'fa-solid fa-database',     route: '/backups' },
        { label: 'Job Batches',     icon: 'fa-solid fa-layer-group',  route: '/job-batches' },
        { label: 'Schedule Logs',   icon: 'fa-solid fa-clock',        route: '/schedule-logs' },
        { label: 'System Messages', icon: 'fa-solid fa-comment-dots', route: '/system-messages' },
      ]
    },
  ];

  constructor(private auth: AuthService, private router: Router) {
    router.events.pipe(filter(e => e instanceof NavigationEnd)).subscribe((e: any) => {
      this.currentRoute.set(e.urlAfterRedirects ?? e.url);
    });
    this.currentRoute.set(router.url);
  }

  isActive(route: string): boolean {
    return this.currentRoute().startsWith(route);
  }

  userName = computed(() => this.auth.currentUser()?.name ?? 'Admin');
  initials = computed(() => {
    const n = this.auth.currentUser()?.name ?? 'A';
    return n.split(' ').map((w: string) => w[0]).slice(0, 2).join('').toUpperCase();
  });

  logout() { this.auth.logout(); this.router.navigate(['/login']); }
}
